import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib';

import { ToastContext, type ToastContextValue, type ToastTone } from './toastContext';

type Toast = {
  id: number;
  tone: ToastTone;
  message: string;
};

/** How long a toast stays on screen. */
const TOAST_DURATION = 4000;

const TONE_STYLES: Record<ToastTone, { icon: ReactNode; className: string }> = {
  success: { icon: <CheckCircle2 size={18} />, className: 'bg-green-600' },
  error: { icon: <AlertCircle size={18} />, className: 'bg-red-600' },
  info: { icon: <Info size={18} />, className: 'bg-gray-900' },
};

/**
 * Transient feedback messages.
 *
 * The region is a polite live region, so a screen reader announces the message
 * without interrupting whatever the user is doing.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, message }]);
      setTimeout(() => dismiss(id), TOAST_DURATION);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-6 left-1/2 z-[100] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4"
      >
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              className={cn(
                'pointer-events-auto flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-float',
                TONE_STYLES[toast.tone].className,
              )}
            >
              {TONE_STYLES[toast.tone].icon}
              <span className="flex-1">{toast.message}</span>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Close"
                className="opacity-70 transition-opacity hover:opacity-100"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
